/* ==========================================================================
   nm.js: gedrag van de nm-secties (nagebouwd uit demo v2).
   - Alle hooks via data-nm-* (nooit via id's), klikken via event delegation.
   - Init bij DOMContentLoaded en opnieuw bij shopify:section:load (theme editor).
   - Winkelwagen via de Shopify AJAX Cart API (/cart/add.js, /cart.js, /cart/change.js).
   - Respecteert prefers-reduced-motion.
   Publieke hulpfuncties staan op window.NM.
   ========================================================================== */
(() => {
  'use strict';
  if (window.NM && window.NM.__loaded) return;

  const d = document;
  const html = d.documentElement;
  if (!window.NM_NOREVEAL) html.classList.add('nm-js');

  const RM = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const SH = window.Shopify || {};
  const ROOT = (SH.routes && SH.routes.root) || '/';
  const DESIGN = !!SH.designMode;

  /* ---------- kleine hulpjes ---------- */
  const $ = (s, c) => (c || d).querySelector(s);
  const $$ = (s, c) => Array.prototype.slice.call((c || d).querySelectorAll(s));
  const within = (scope, s) => {
    const list = $$(s, scope);
    if (scope && scope !== d && scope.matches && scope.matches(s)) list.unshift(scope);
    return list;
  };
  const restart = (el, cls) => {
    if (!el || RM) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
  };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const inView = (el) => {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight && r.width > 0;
  };
  const focusSoft = (el) => { if (el && el.focus) { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } } };
  const store = {
    get(type, k) { try { return window[type].getItem(k); } catch (e) { return null; } },
    set(type, k, v) { try { window[type].setItem(k, v); } catch (e) { /* privé-venster of geblokkeerd: geen probleem */ } }
  };

  /* ---------- configuratie (uit nm-overlays: <script type="application/json" data-nm-config>) ---------- */
  const T0 = {
    remove: 'Verwijderen',
    empty: 'Uw winkelwagen is nog leeg.',
    shop: 'Bekijk de body oil',
    freeLeft: 'Nog [bedrag] tot gratis verzending',
    freeDone: 'Uw verzending is gratis',
    upsell: 'Maak er [aantal] van voor [bedrag] extra',
    upsellSave: 'Neem [aantal] en bespaar [bedrag]',
    savings: 'U bespaart [bedrag] met een tijdelijke korting',
    soldOut: 'Uitverkocht',
    error: 'Er ging iets mis. Probeer het opnieuw.',
    newsOk: 'Dank u wel, u staat op de lijst.',
    newsError: 'Dit e-mailadres klopt nog niet helemaal.'
  };
  const COMPARE = {}; // variant-id -> compare_at_price in centen
  const PRODUCTS = {}; // handle -> Promise<product.js>
  const PROD = {}; // handle -> product.js (opgehaald)
  let CFG = { t: T0 };
  let CART = null;
  let lastFocus = null;
  let uid = 0;

  function decode(str) {
    const ta = d.createElement('textarea');
    ta.innerHTML = String(str || '');
    return ta.value;
  }

  function readConfig() {
    const el = $('[data-nm-config]');
    let c = {};
    if (el) {
      try { c = JSON.parse(el.textContent || '{}') || {}; } catch (e) { c = {}; }
    }
    const t = Object.assign({}, T0);
    Object.keys(c.t || {}).forEach((k) => { if (c.t[k]) t[k] = c.t[k]; });
    c.t = t;
    if (c.moneyFormat) c.moneyFormat = decode(c.moneyFormat).replace(/<[^>]*>/g, '');
    (c.bundle || []).forEach((v) => { if (v && v.compare > v.price) COMPARE[v.id] = v.compare; });
    CFG = c;
  }

  /* ---------- geld ---------- */
  function money(cents) {
    cents = Math.round(Number(cents) || 0);
    const fmt = CFG.moneyFormat || '€{{amount_with_comma_separator}}';
    const m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const num = (prec, th, dec) => {
      const parts = (Math.abs(cents) / 100).toFixed(prec).split('.');
      return (cents < 0 ? '-' : '') + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th) + (parts[1] ? dec + parts[1] : '');
    };
    let v;
    switch (m ? m[1] : '') {
      case 'amount': v = num(2, ',', '.'); break;
      case 'amount_no_decimals': v = num(0, ',', '.'); break;
      case 'amount_with_comma_separator': v = num(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = num(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = num(2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': v = num(0, ' ', ','); break;
      case 'amount_with_space_separator': v = num(2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': v = num(2, ' ', '.'); break;
      default: v = num(2, '.', ',');
    }
    return m ? fmt.replace(m[0], v) : v;
  }

  /* ---------- AJAX Cart API ---------- */
  async function api(path, body) {
    const opts = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body !== undefined) {
      opts.method = 'POST';
      opts.headers['Content-Type'] = 'application/json';
      opts.headers['X-Requested-With'] = 'XMLHttpRequest';
      opts.body = JSON.stringify(body);
    }
    const r = await fetch(ROOT + path, opts);
    let j = null;
    try { j = await r.json(); } catch (e) { j = null; }
    if (!r.ok) {
      const err = new Error((j && (j.description || j.message)) || 'HTTP ' + r.status);
      err.status = r.status;
      throw err;
    }
    return j;
  }

  function productJson(handle) {
    if (!handle) return Promise.resolve(null);
    if (!PRODUCTS[handle]) {
      PRODUCTS[handle] = fetch(ROOT + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          if (p && p.variants) {
            PROD[handle] = p;
            p.variants.forEach((v) => { if (v.compare_at_price > v.price) COMPARE[v.id] = v.compare_at_price; });
          }
          return p;
        })
        .catch(() => null);
    }
    return PRODUCTS[handle];
  }

  function loadProducts(cart) {
    if (!cart || !cart.items) return;
    const handles = cart.items.map((i) => i.handle).filter((h, i, a) => h && a.indexOf(h) === i && !(h in PRODUCTS));
    if (!handles.length) return;
    Promise.all(handles.map(productJson)).then(() => { if (CART === cart) renderCart(cart, { keepBar: true }); });
  }

  const drawer = () => $('[data-nm-drawer]');

  function bottleHTML() {
    const tpl = $('template[data-nm-bottle-tpl]');
    if (!tpl) return '';
    uid += 1;
    return tpl.innerHTML.replace(/nmuid/g, 'c' + uid);
  }

  function imgUrl(src, w) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function lineHTML(it) {
    let cmp = (COMPARE[it.variant_id] || 0) * it.quantity;
    if (!(cmp > it.final_line_price) && it.original_line_price > it.final_line_price) cmp = it.original_line_price;
    const media = it.image
      ? '<img src="' + esc(imgUrl(it.image, 200)) + '" alt="' + esc(it.product_title) + '" width="74" height="74" loading="lazy">'
      : '<div class="nm-bottle">' + bottleHTML() + '</div>';
    const variant = it.product_has_only_default_variant ? '' : it.variant_title || '';
    const sub = ((it.quantity > 1 ? it.quantity + ' × ' : '') + variant).trim();
    return '<div class="nm-li" data-key="' + esc(it.key) + '" data-variant-id="' + esc(it.variant_id) + '">' +
      '<div class="nm-lt">' + media + '</div>' +
      '<div class="nm-lb"><span class="nm-ln">' + esc(it.product_title) + '</span>' + (sub ? '<small>' + esc(sub) + '</small>' : '') +
      '<div class="nm-lq"><div class="nm-qty">' +
      '<button type="button" data-nm-qty="' + esc(it.key) + '" data-delta="-1" aria-label="Eén minder">−</button>' +
      '<span>' + it.quantity + '</span>' +
      '<button type="button" data-nm-qty="' + esc(it.key) + '" data-delta="1" aria-label="Eén meer">+</button>' +
      '</div><button type="button" class="nm-li-rm" data-nm-remove="' + esc(it.key) + '">' + esc(CFG.t.remove) + '</button></div></div>' +
      '<div class="nm-lp"><b>' + esc(money(it.final_line_price)) + '</b>' + (cmp > it.final_line_price ? '<s>' + esc(money(cmp)) + '</s>' : '') + '</div>' +
      '</div>';
  }

  function renderCart(cart, opts) {
    opts = opts || {};
    if (!cart || !cart.items) return;
    CART = cart;
    $$('[data-nm-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    $$('[data-nm-cart-count-text]').forEach((el) => {
      const one = el.getAttribute('data-one') || 'artikel';
      const many = el.getAttribute('data-many') || 'artikelen';
      el.textContent = '(' + cart.item_count + ' ' + (cart.item_count === 1 ? one : many) + ')';
    });
    $$('[data-nm-cart-total]').forEach((el) => { el.textContent = money(cart.total_price); });
    renderSavings(cart);
    const dr = drawer();
    if (dr) dr.classList.toggle('nm-empty', !cart.item_count);
    $$('[data-nm-cart-lines]').forEach((box) => {
      box.innerHTML = cart.items.length
        ? cart.items.map(lineHTML).join('')
        : '<div class="nm-empty-msg"><p>' + esc(CFG.t.empty) + '</p><a class="nm-wbtn" href="' + esc(CFG.shopUrl || ROOT + 'collections/all') + '">' + esc(CFG.t.shop) + '</a></div>';
    });
    if (!opts.keepBar) renderFreeship(cart.total_price, opts.animateBar);
    renderUpsell(cart);
    $$('[data-nm-gift]').forEach((g) => {
      const name = g.getAttribute('data-nm-gift') || 'Cadeau';
      if (!g._nmBusy) g.checked = !!(cart.attributes && cart.attributes[name]);
    });
    if (opts.bounce) restart($('.nm-li[data-variant-id="' + opts.bounce + '"]'), 'nm-bounce');
  }

  /* besparing in de lade: verschil tussen de van-prijzen en wat de klant nu betaalt */
  function renderSavings(cart) {
    let full = 0;
    (cart.items || []).forEach((it) => {
      const cmp = (COMPARE[it.variant_id] || 0) * it.quantity;
      full += cmp > it.original_line_price ? cmp : it.original_line_price;
    });
    const save = Math.max(0, full - cart.total_price);
    $$('[data-nm-save]').forEach((box) => {
      const out = box.querySelector('[data-nm-save-text]');
      const tpl = box.getAttribute('data-nm-text') || CFG.t.savings;
      if (save > 0 && cart.item_count) {
        if (out) out.textContent = String(tpl).replace('[bedrag]', money(save));
        box.hidden = false;
      } else {
        box.hidden = true;
      }
    });
    $$('[data-nm-savings-row]').forEach((row) => {
      const out = row.querySelector('[data-nm-savings-total]');
      if (out) out.textContent = money(save);
      row.hidden = !(save > 0 && cart.item_count);
    });
  }

  function renderFreeship(total, animate) {
    const holder = $('[data-nm-freeship]');
    if (!holder) return;
    const rate = Number(SH.currency && SH.currency.rate) || 1;
    const th = Math.round((Number(holder.getAttribute('data-nm-freeship')) || 0) * rate);
    const left = th - total;
    $$('[data-nm-freeship-text]').forEach((el) => {
      el.textContent = left > 0
        ? (el.getAttribute('data-nm-left') || CFG.t.freeLeft).replace('[bedrag]', money(left))
        : el.getAttribute('data-nm-done') || CFG.t.freeDone;
    });
    const pct = th > 0 ? Math.min(100, Math.max(0, (total / th) * 100)) : 100;
    $$('[data-nm-freeship-ic]').forEach((ic) => { ic.style.left = pct + '%'; ic.classList.toggle('nm-fs-done', left <= 0); });
    $$('[data-nm-freeship-note]').forEach((n) => { n.hidden = left > 0; });
    $$('[data-nm-freeship-bar]').forEach((b) => {
      clearTimeout(b._nmT);
      if (animate && !RM) {
        b.style.transition = 'none';
        b.style.width = '0%';
        void b.offsetWidth;
        b.style.transition = '';
        b._nmT = setTimeout(() => { b.style.width = pct + '%'; }, 250);
      } else {
        b.style.width = pct + '%';
      }
    });
  }

  /* upsell: de eerste regel van het bundelproduct die nog een grotere bundel heeft */
  function renderUpsell(cart) {
    const boxes = $$('[data-nm-upsell]');
    if (!boxes.length) return;
    let hit = null;
    for (const it of cart.items) {
      let variants = null;
      if (CFG.bundleProductId) {
        if (Number(it.product_id) === Number(CFG.bundleProductId) && CFG.bundle && CFG.bundle.length) variants = CFG.bundle;
      } else if (PROD[it.handle] && PROD[it.handle].variants && PROD[it.handle].variants.length > 1) {
        variants = PROD[it.handle].variants.map((v) => ({ id: v.id, title: v.public_title || v.title, price: v.price, available: v.available }));
      }
      if (!variants) continue;
      const i = variants.findIndex((v) => Number(v.id) === Number(it.variant_id));
      if (i < 0) continue;
      const flessen = (v, idx) => { const m = String((v && v.title) || '').match(/\d+/); return m ? Number(m[0]) : idx + 1; };
      const heeft = flessen(variants[i], i) * Number(it.quantity || 1);
      /* eerst kijken of er een set is met minstens evenveel flessen die goedkoper is dan wat er nu in de wagen ligt */
      let next = null;
      let bespaart = false;
      for (let j = i + 1; j < variants.length; j++) {
        const v = variants[j];
        if (v.available === false) continue;
        if (flessen(v, j) >= heeft && Number(v.price) < Number(it.final_line_price)) { next = v; bespaart = true; break; }
      }
      if (!next && Number(it.quantity) === 1) next = variants.slice(i + 1).find((v) => v.available !== false);
      if (!next) continue;
      hit = { it, cur: variants[i], next, bespaart: bespaart };
      break;
    }
    boxes.forEach((box) => {
      if (!hit) {
        box.classList.add('nm-hidden');
        return;
      }
      box.classList.remove('nm-hidden');
      const diff = hit.bespaart
        ? Math.max(0, Number(hit.it.final_line_price) - Number(hit.next.price))
        : Math.max(0, (Number(hit.next.price) - Number(hit.cur.price)) * hit.it.quantity);
      const sjabloon = hit.bespaart
        ? (box.getAttribute('data-nm-save-text') || CFG.t.upsellSave)
        : (box.getAttribute('data-nm-text') || CFG.t.upsell);
      const txt = String(sjabloon).replace('[aantal]', hit.next.title).replace('[bedrag]', money(diff));
      const out = box.querySelector('[data-nm-upsell-text]') || box.querySelector('span');
      if (out) out.textContent = txt;
      const im = box.querySelector('[data-nm-ups-img]');
      if (im && !im.dataset.nmFor || (im && im.dataset.nmFor !== String(hit.next.id))) {
        const src = hit.it.image ? imgUrl(hit.it.image, 200) : '';
        im.innerHTML = src
          ? '<img src="' + esc(src) + '" alt="" width="64" height="64" loading="lazy">'
          : '<div class="nm-bottle">' + bottleHTML() + '</div>';
        im.dataset.nmFor = String(hit.next.id);
      }
      const ti = box.querySelector('[data-nm-ups-title]');
      if (ti) ti.textContent = hit.it.product_title + ', ' + hit.next.title;
      const pr = box.querySelector('[data-nm-ups-price]');
      if (pr) pr.textContent = money(Number(hit.next.price));
      const cm = box.querySelector('[data-nm-ups-cmp]');
      if (cm) {
        const c = Number(hit.next.compare || COMPARE[hit.next.id] || 0);
        cm.textContent = c > Number(hit.next.price) ? money(c) : '';
      }
      box.setAttribute('data-key', hit.it.key);
      box.setAttribute('data-next', hit.next.id);
      box.setAttribute('data-qty', hit.bespaart ? 1 : hit.it.quantity);
    });
  }

  async function refreshCart(opts) {
    try {
      const cart = await api('cart.js');
      renderCart(cart, opts);
      loadProducts(cart);
      return cart;
    } catch (e) {
      return null;
    }
  }

  function bump() {
    $$('.nm-bubble').forEach((b) => restart(b, 'nm-bump'));
  }

  function emit(cart) {
    d.dispatchEvent(new CustomEvent('nm:cart:updated', { detail: { cart } }));
  }

  function flash(btn, msg) {
    if (!btn) return;
    if (btn.getAttribute('data-nm-label') == null) btn.setAttribute('data-nm-label', btn.innerHTML);
    btn.textContent = msg;
    clearTimeout(btn._nmFlash);
    btn._nmFlash = setTimeout(() => {
      btn.innerHTML = btn.getAttribute('data-nm-label');
      btn.removeAttribute('data-nm-label');
    }, 2600);
  }

  function openCart(refresh) {
    const dr = drawer();
    if (!dr) return false;
    if (!d.body.classList.contains('nm-cart')) lastFocus = d.activeElement;
    d.body.classList.add('nm-cart');
    dr.setAttribute('aria-hidden', 'false');
    if (CART) renderFreeship(CART.total_price, true);
    const x = dr.querySelector('[data-nm-cart-close]');
    if (x) setTimeout(() => focusSoft(x), 60);
    if (refresh) refreshCart({ animateBar: true });
    return true;
  }

  function closeCart() {
    if (!d.body.classList.contains('nm-cart')) return;
    if (lastFocus && d.contains(lastFocus)) focusSoft(lastFocus);
    d.body.classList.remove('nm-cart');
    const dr = drawer();
    if (dr) dr.setAttribute('aria-hidden', 'true');
  }

  /* ---------- bundeltegels ---------- */
  function selectedTile() {
    return $('[data-nm-bundles] .nm-tile.nm-on') || $('[data-nm-bundles] .nm-tile:not(:disabled)');
  }

  function stickyInfo(tile) {
    tile = tile || selectedTile();
    if (!tile) return;
    const t1 = tile.querySelector('.nm-t1');
    const label = t1 ? t1.textContent.trim() : tile.getAttribute('data-qty') + ' ×';
    const price = tile.getAttribute('data-price-cents');
    const txt = label + (price ? ' · ' + money(price) : '');
    $$('[data-nm-sticky-info]').forEach((el) => { el.textContent = txt; });
  }

  function selectTile(tile, anim) {
    const wrap = tile.closest('[data-nm-bundles]');
    if (!wrap || tile.disabled) return;
    $$('.nm-tile', wrap).forEach((t) => {
      const on = t === tile;
      t.classList.toggle('nm-on', on);
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const radio = tile.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;
    if (anim) restart(tile, 'nm-pop');
    const form = wrap.closest('form');
    const idInput = form && form.querySelector('[name="id"]');
    if (idInput) idInput.value = tile.getAttribute('data-variant-id');
    stickyInfo(tile);
    wrap.dispatchEvent(new CustomEvent('nm:bundle:change', {
      bubbles: true,
      detail: { variantId: Number(tile.getAttribute('data-variant-id')), qty: Number(tile.getAttribute('data-qty')) }
    }));
  }

  function variantFor(btn) {
    const own = btn.getAttribute('data-variant-id');
    const tile = selectedTile();
    const tileId = tile && tile.getAttribute('data-variant-id');
    const form = btn.closest('form');
    const idInput = form && form.querySelector('[name="id"]');
    const formId = idInput && idInput.value;
    const main = btn.hasAttribute('data-nm-atc-main') || !!btn.closest('[data-nm-sticky]');
    return main ? tileId || own || formId : own || formId || tileId;
  }

  async function addToCart(btn) {
    const id = Number(variantFor(btn));
    if (!id || btn.disabled || btn.classList.contains('nm-loading')) return;
    btn.classList.add('nm-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      await api('cart/add.js', { id, quantity: 1 });
      const cart = await api('cart.js');
      renderCart(cart, { bounce: id, animateBar: true });
      loadProducts(cart);
      bump();
      emit(cart);
      if (!openCart(false)) window.location.href = ROOT + 'cart';
    } catch (e) {
      flash(btn, e.status === 422 ? CFG.t.soldOut : CFG.t.error);
    } finally {
      btn.classList.remove('nm-loading');
      btn.removeAttribute('aria-busy');
    }
  }

  /* aantal in de lade: plus en min, zoals bij Skincu en Smooche */
  async function changeQty(btn) {
    const key = btn.getAttribute('data-nm-qty');
    const delta = Number(btn.getAttribute('data-delta')) || 0;
    if (!key || !delta || btn.disabled) return;
    const item = CART && CART.items.find((i) => i.key === key);
    const qty = Math.max(0, (item ? Number(item.quantity) : 1) + delta);
    const row = btn.closest('.nm-li');
    if (row) row.classList.add('nm-busy');
    btn.disabled = true;
    try {
      const cart = await api('cart/change.js', { id: key, quantity: qty });
      renderCart(cart);
      emit(cart);
    } catch (e) {
      btn.disabled = false;
      if (row) row.classList.remove('nm-busy');
      flash(btn, CFG.t.error);
    }
  }

  async function removeLine(btn) {
    const key = btn.getAttribute('data-nm-remove');
    if (!key) return;
    btn.disabled = true;
    try {
      const cart = await api('cart/change.js', { id: key, quantity: 0 });
      renderCart(cart);
      emit(cart);
    } catch (e) {
      btn.disabled = false;
      flash(btn, CFG.t.error);
    }
  }

  async function doUpsell(box, btn) {
    const key = box.getAttribute('data-key');
    const next = Number(box.getAttribute('data-next'));
    const qty = Number(box.getAttribute('data-qty')) || 1;
    if (!key || !next || (btn && btn.classList.contains('nm-loading'))) return;
    const item = CART && CART.items.find((i) => i.key === key);
    if (btn) btn.classList.add('nm-loading');
    try {
      const add = { id: next, quantity: qty };
      if (item && item.properties && Object.keys(item.properties).length) add.properties = item.properties;
      await api('cart/add.js', { items: [add] });
      const cart = await api('cart/change.js', { id: key, quantity: 0 });
      renderCart(cart, { bounce: next });
      bump();
      emit(cart);
      const tile = $('[data-nm-bundles] .nm-tile[data-variant-id="' + next + '"]');
      if (tile) selectTile(tile, true);
    } catch (e) {
      flash(btn, e.status === 422 ? CFG.t.soldOut : CFG.t.error);
    } finally {
      if (btn) btn.classList.remove('nm-loading');
    }
  }

  async function setGift(input) {
    const name = input.getAttribute('data-nm-gift') || 'Cadeau';
    const attributes = {};
    attributes[name] = input.checked ? 'Ja' : '';
    input._nmBusy = (input._nmBusy || 0) + 1;
    try {
      CART = await api('cart/update.js', { attributes });
    } catch (e) {
      input.checked = !input.checked;
    } finally {
      input._nmBusy -= 1;
    }
  }

  /* ---------- uitklapblokken ---------- */
  function toggleAcc(head) {
    const it = head.closest('.nm-acc-i');
    const body = it && it.querySelector('.nm-acc-b');
    if (!body) return;
    const open = !it.classList.contains('nm-open');
    head.setAttribute('aria-expanded', open ? 'true' : 'false');
    clearTimeout(body._nmT);
    if (RM) {
      it.classList.toggle('nm-open', open);
      body.style.maxHeight = '';
      return;
    }
    if (open) {
      body.style.maxHeight = '0px';
      it.classList.add('nm-open');
      void body.offsetHeight;
      body.style.maxHeight = body.scrollHeight + 'px';
      body._nmT = setTimeout(() => { if (it.classList.contains('nm-open')) body.style.maxHeight = ''; }, 480);
    } else {
      body.style.maxHeight = body.scrollHeight + 'px';
      void body.offsetHeight;
      it.classList.remove('nm-open');
      body.style.maxHeight = '0px';
      body._nmT = setTimeout(() => { body.style.maxHeight = ''; }, 480);
    }
  }

  /* ---------- galerij ---------- */
  function galIndex(g) {
    const i = $$('.nm-slide', g).findIndex((s) => s.classList.contains('nm-on'));
    return i < 0 ? 0 : i;
  }

  function galGo(g, i) {
    const slides = $$('.nm-slide', g);
    const ths = $$('.nm-th', g);
    const n = slides.length;
    if (!n) return;
    i = ((i % n) + n) % n;
    slides.forEach((s, k) => {
      const on = k === i;
      s.classList.toggle('nm-on', on);
      s.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (!on) s.querySelectorAll('video').forEach((v) => { try { v.pause(); } catch (e) { /* */ } });
    });
    ths.forEach((t, k) => {
      t.classList.toggle('nm-on', k === i);
      t.setAttribute('aria-current', k === i ? 'true' : 'false');
    });
    const th = ths[i];
    if (th && th.parentElement) {
      const row = th.parentElement;
      const r = th.getBoundingClientRect();
      const rr = row.getBoundingClientRect();
      if (r.left < rr.left) row.scrollLeft -= rr.left - r.left;
      else if (r.right > rr.right) row.scrollLeft += r.right - rr.right;
    }
  }

  let swipe = null;
  d.addEventListener('touchstart', (e) => {
    const t = e.target;
    const g = t && t.closest && t.closest('[data-nm-gallery]');
    if (!g || t.closest('.nm-gal-th, .nm-th-row')) { swipe = null; return; }
    swipe = { g, x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, { passive: true });
  d.addEventListener('touchend', (e) => {
    if (!swipe) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - swipe.x;
    const dy = t.clientY - swipe.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) galGo(swipe.g, galIndex(swipe.g) + (dx < 0 ? 1 : -1));
    swipe = null;
  }, { passive: true });

  /* ---------- aankondigingsbalk ---------- */
  function annSlides(a) {
    const track = a.querySelector('[data-nm-ann-track]');
    if (!track) return null;
    return track.classList.contains('nm-ann-track') && track.firstElementChild ? track.firstElementChild : track;
  }

  function annGo(a, dir, abs) {
    const s = annSlides(a);
    if (!s) return;
    const n = s.children.length;
    if (!n) return;
    const cur = a._nmI || 0;
    a._nmI = abs ? ((dir % n) + n) % n : (((cur + dir) % n) + n) % n;
    s.style.transform = 'translateX(-' + a._nmI * 100 + '%)';
  }

  function initAnn(a) {
    clearInterval(a._nmTimer);
    const s = annSlides(a);
    if (!s || s.children.length < 2 || RM) return;
    if (!a._nmBound) {
      a._nmBound = true;
      a.addEventListener('mouseenter', () => { a._nmHover = true; });
      a.addEventListener('mouseleave', () => { a._nmHover = false; });
      a.addEventListener('focusin', () => { a._nmHover = true; });
      a.addEventListener('focusout', () => { a._nmHover = false; });
    }
    const ms = Number(a.getAttribute('data-nm-ann-interval')) || 3500;
    a._nmTimer = setInterval(() => {
      if (!d.contains(a)) { clearInterval(a._nmTimer); return; }
      if (!d.hidden && !a._nmHover && !a._nmHold) annGo(a, 1);
    }, ms);
  }

  /* ---------- testimonial-slider ---------- */
  function sliderDots(s) {
    const n = s.nextElementSibling;
    if (n && n.matches('[data-nm-dots]')) return n;
    const sec = s.closest('.nm') || s.parentElement;
    return sec ? sec.querySelector('[data-nm-dots]') : null;
  }

  function sliderFor(dots) {
    const p = dots.previousElementSibling;
    if (p && p.matches('[data-nm-slider]')) return p;
    const sec = dots.closest('.nm') || dots.parentElement;
    return sec ? sec.querySelector('[data-nm-slider]') : null;
  }

  function setDots(s, i) {
    const dots = sliderDots(s);
    if (dots) Array.prototype.forEach.call(dots.children, (dt, k) => dt.classList.toggle('nm-on', k === i));
  }

  function sliderGo(s, i) {
    const cards = s.children;
    const n = cards.length;
    if (!n) return;
    i = ((i % n) + n) % n;
    s._nmI = i;
    const pad = parseFloat(getComputedStyle(s).paddingLeft) || 0;
    const left = s.scrollLeft + cards[i].getBoundingClientRect().left - s.getBoundingClientRect().left - pad;
    s.scrollTo({ left, behavior: RM ? 'auto' : 'smooth' });
    setDots(s, i);
  }

  function initSlider(s) {
    const dots = sliderDots(s);
    const n = s.children.length;
    if (dots && dots.children.length !== n) {
      dots.innerHTML = Array.from({ length: n }, (_, k) => '<i class="' + (k ? '' : 'nm-on') + '"></i>').join('');
    }
    if (s._nmBound) return;
    s._nmBound = true;
    s._nmI = 0;
    let raf = 0;
    s.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const cards = Array.prototype.slice.call(s.children);
        if (!cards.length) return;
        let best = 0;
        if (s.scrollLeft >= s.scrollWidth - s.clientWidth - 2) {
          best = cards.length - 1;
        } else {
          const base = s.getBoundingClientRect().left + (parseFloat(getComputedStyle(s).paddingLeft) || 0);
          let bd = Infinity;
          cards.forEach((c, k) => {
            const dist = Math.abs(c.getBoundingClientRect().left - base);
            if (dist < bd) { bd = dist; best = k; }
          });
        }
        s._nmI = best;
        setDots(s, best);
      });
    }, { passive: true });
    const hold = () => { s._nmHold = Date.now(); };
    s.addEventListener('pointerdown', hold, { passive: true });
    s.addEventListener('wheel', hold, { passive: true });
    s.addEventListener('mouseenter', () => { s._nmHover = true; });
    s.addEventListener('mouseleave', () => { s._nmHover = false; });
    if (RM) return;
    const ms = Number(s.getAttribute('data-nm-interval')) || 4200;
    s._nmTimer = setInterval(() => {
      if (!d.contains(s)) { clearInterval(s._nmTimer); return; }
      if (d.hidden || s._nmHover || (s._nmHold && Date.now() - s._nmHold < 8000)) return;
      if (s.children.length < 2 || s.scrollWidth <= s.clientWidth + 2 || !inView(s)) return;
      sliderGo(s, (s._nmI || 0) + 1);
    }, ms);
  }

  /* ---------- scroll-animaties: reveal, tellers, ringen, balken ---------- */
  const REVEAL = '.nm-rv, .nm-tl-it, [data-nm-count], [data-nm-ring], .nm-rv-sum, .nm-hist';
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        show(e.target);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' })
    : null;

  function observe(scope) {
    within(scope, REVEAL).forEach((el) => {
      if (el._nmSeen) return;
      el._nmSeen = true;
      if (io) io.observe(el);
      else show(el);
    });
  }

  function show(t) {
    t.classList.add('nm-in');
    if (t.hasAttribute('data-nm-count')) countUp(t);
    if (t.hasAttribute('data-nm-ring')) ring(t);
    $$('[data-nm-count]', t).forEach(countUp);
    $$('[data-nm-ring]', t).forEach(ring);
    $$('.nm-hist i b, [data-nm-w]', t).forEach(bar);
  }

  function bar(b) {
    const w = b.getAttribute('data-nm-w') || b.getAttribute('data-w');
    if (w != null && w !== '') b.style.width = parseFloat(w) + '%';
  }

  function countUp(el) {
    if (!el || el._nmCounted) return;
    el._nmCounted = true;
    const raw = String(el.getAttribute('data-nm-count') || '').trim().replace(/[^\d.,-]/g, '');
    let norm = raw;
    if (raw.indexOf(',') > -1) norm = raw.replace(/\./g, '').replace(',', '.');
    else if (/^\d{1,3}(\.\d{3})+$/.test(raw)) norm = raw.replace(/\./g, '');
    const end = parseFloat(norm) || 0;
    const dec = (norm.split('.')[1] || '').length;
    const fmt = (v) => {
      try { return v.toLocaleString('nl-NL', { minimumFractionDigits: dec, maximumFractionDigits: dec }); } catch (e) { return String(v); }
    };
    if (RM || !end) { el.textContent = fmt(end); return; }
    const dur = 1400;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      el.textContent = fmt(p < 1 ? end * (1 - Math.pow(1 - p, 3)) : end);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  const RING_C = 2 * Math.PI * 40;
  function prepRing(el) {
    if (el._nmPrep) return;
    el._nmPrep = true;
    const pct = parseFloat(el.getAttribute('data-nm-ring')) || 0;
    let svg = el.querySelector('svg');
    if (!svg) {
      el.insertAdjacentHTML('afterbegin', '<svg viewBox="0 0 96 96" aria-hidden="true" focusable="false"><circle class="nm-bgc" cx="48" cy="48" r="40" fill="none" stroke-width="8"/></svg>');
      svg = el.querySelector('svg');
    }
    if (!svg.querySelector('.nm-fg')) {
      svg.insertAdjacentHTML('beforeend', '<circle class="nm-fg" cx="48" cy="48" r="40" fill="none" stroke-width="8" stroke-dasharray="' + RING_C + '" stroke-dashoffset="' + RING_C + '"/>');
    }
    if (!el.querySelector('b')) el.insertAdjacentHTML('beforeend', '<b><span data-nm-count="' + pct + '">0</span>%</b>');
    if (!RM) {
      const cnt = el.querySelector('[data-nm-count]');
      if (cnt && !cnt._nmCounted) cnt.textContent = '0';
    }
  }

  function ring(el) {
    if (el._nmRing) return;
    el._nmRing = true;
    prepRing(el);
    const pct = Math.max(0, Math.min(100, parseFloat(el.getAttribute('data-nm-ring')) || 0));
    const fg = el.querySelector('.nm-fg');
    if (fg) {
      fg.setAttribute('stroke-dasharray', RING_C);
      const target = RING_C * (1 - pct / 100);
      if (RM) fg.style.strokeDashoffset = target;
      else requestAnimationFrame(() => requestAnimationFrame(() => { fg.style.strokeDashoffset = target; }));
    }
    countUp(el.querySelector('[data-nm-count]'));
  }

  /* ---------- scroll: header-schaduw, tijdlijn, parallax, sticky-fallback ---------- */
  const L = { hdr: [], tl: [], par: [], sticky: null, main: null };
  function cacheLists() {
    L.hdr = $$('[data-nm-hdr]');
    L.tl = $$('[data-nm-tl]');
    L.par = RM ? [] : $$('[data-nm-par]');
    L.sticky = $('[data-nm-sticky]');
    L.main = $('[data-nm-atc-main]');
  }

  let ticking = false;
  function onScroll() {
    ticking = false;
    /* Horizon scrollt op desktop (vanaf 990px) in .page-wrapper in plaats van in het venster */
    const pw = d.querySelector('.page-wrapper');
    const y = Math.max(window.scrollY || window.pageYOffset || 0, pw ? pw.scrollTop : 0);
    const vh = window.innerHeight;
    L.hdr.forEach((h) => h.classList.toggle('nm-scrolled', y > 10));
    L.tl.forEach((tl) => {
      const fill = tl.querySelector('[data-nm-tl-fill]');
      if (!fill || !tl.offsetParent) return;
      const r = tl.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, (vh * 0.6 - r.top) / (r.height || 1)));
      fill.style.height = p * 100 + '%';
    });
    L.par.forEach((el) => {
      const r = (el.parentElement || el).getBoundingClientRect();
      if (r.bottom < -300 || r.top > vh + 300) return;
      el.style.transform = 'translateY(' + ((r.top - vh / 2) * (Number(el.getAttribute('data-nm-par')) || 0)).toFixed(1) + 'px)';
    });
    if (L.sticky && !L.main) L.sticky.classList.toggle('nm-show', y > 700);
  }
  const requestScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(onScroll);
    }
  };
  window.addEventListener('scroll', requestScroll, { passive: true });
  /* capture: vangt ook het scrollen van .page-wrapper (Horizon op desktop), scroll-events bubbelen niet */
  d.addEventListener('scroll', requestScroll, { passive: true, capture: true });
  window.addEventListener('resize', requestScroll, { passive: true });

  /* ---------- sticky knop ---------- */
  let stickyIO = null;
  function initSticky() {
    if (stickyIO) { stickyIO.disconnect(); stickyIO = null; }
    if (!L.sticky) return;
    stickyInfo();
    if (L.main && 'IntersectionObserver' in window) {
      const st = L.sticky;
      stickyIO = new IntersectionObserver((es) => {
        const e = es[es.length - 1];
        const visible = e.isIntersecting || e.boundingClientRect.top > 0;
        st.classList.toggle('nm-show', !visible);
      });
      stickyIO.observe(L.main);
    }
  }

  /* ---------- kraskaart-popup ---------- */
  const K_SEEN = 'nm-pop-seen';
  const K_CLAIMED = 'nm-pop-claimed';
  const K_UNLOCK = 'nm-unlock-hidden';
  const seenRecently = () => Date.now() - (Number(store.get('localStorage', K_SEEN)) || 0) < 864e5;

  function showUnlock(on) {
    const u = $('[data-nm-unlock]');
    if (!u) return;
    if (on && (store.get('localStorage', K_CLAIMED) || store.get('sessionStorage', K_UNLOCK))) on = false;
    u.classList.toggle('nm-show', !!on);
  }

  function openPop(pop, silent) {
    pop = pop || $('[data-nm-pop]');
    if (!pop) return;
    if (!pop.classList.contains('nm-show')) lastFocus = d.activeElement;
    pop.classList.add('nm-show');
    pop.setAttribute('aria-hidden', 'false');
    html.classList.add('nm-lock'); // schermvullend: pagina eronder staat stil
    if (!silent) store.set('localStorage', K_SEEN, String(Date.now()));
    showUnlock(false);
    const cv = pop.querySelector('[data-nm-scratch]');
    if (cv && !cv._nmPainted && !pop._nmRevealed) requestAnimationFrame(() => paintScratch(cv, 0));
    const x = pop.querySelector('[data-nm-pop-close]');
    if (x) setTimeout(() => focusSoft(x), 80);
  }

  function closePop(pop, silent) {
    pop = pop || $('[data-nm-pop].nm-show');
    if (!pop || !pop.classList.contains('nm-show')) return;
    if (!silent) showUnlock(true);
    if (lastFocus && d.contains(lastFocus)) focusSoft(lastFocus);
    pop.classList.remove('nm-show');
    pop.setAttribute('aria-hidden', 'true');
    html.classList.remove('nm-lock');
  }


  function paintScratch(cv, tries) {
    // layoutmaat (offsetWidth) in plaats van getBoundingClientRect: tijdens de open-animatie is de kaart nog verschoven/geschaald
    const W = cv.offsetWidth, H = cv.offsetHeight;
    if (!W || !H) {
      if (tries < 30) requestAnimationFrame(() => paintScratch(cv, tries + 1));
      return;
    }
    const ctx = cv.getContext && cv.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    cv.width = Math.round(W * dpr);
    cv.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    // goudfolie: metaalachtig verloop, zachte glans en fijne diagonale lijntjes
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, '#B38B4A'); g.addColorStop(0.28, '#E6D19C'); g.addColorStop(0.5, '#C29E5E');
    g.addColorStop(0.72, '#EFDDB0'); g.addColorStop(1, '#A9803F');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    const gl = ctx.createRadialGradient(W * 0.3, H * 0.2, 0, W * 0.3, H * 0.2, W * 0.7);
    gl.addColorStop(0, 'rgba(255,248,226,.45)'); gl.addColorStop(1, 'rgba(255,248,226,0)');
    ctx.fillStyle = gl;
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = 'rgba(255,255,255,.16)';
    ctx.lineWidth = 1;
    for (let x = -H; x < W; x += 7) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x + H, H); ctx.stroke(); }
    ctx.strokeStyle = 'rgba(90,22,40,.35)';
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(9.5, 9.5, W - 19, H - 19, 9);
    else ctx.rect(9.5, 9.5, W - 19, H - 19);
    ctx.stroke();
    const cs = getComputedStyle(html);
    const serif = (cs.getPropertyValue('--nm-serif') || '').trim() || 'Georgia, serif';
    const fam = (cs.getPropertyValue('--nm-sans') || '').trim() || 'Arial, sans-serif';
    const sub = cv.getAttribute('data-nm-sublabel') || '';
    const big = Math.round(Math.max(30, Math.min(46, W / 9)));
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#5A1628';
    ctx.font = 'italic 400 ' + big + 'px ' + serif;
    ctx.fillText(cv.getAttribute('data-nm-label') || 'Kras hier', W / 2, H / 2 - (sub ? big * 0.3 : 0));
    if (sub) {
      ctx.font = '500 ' + (big > 38 ? 12 : 11) + 'px ' + fam;
      if ('letterSpacing' in ctx) ctx.letterSpacing = '3px';
      ctx.fillText(sub, W / 2, H / 2 + big * 0.62);
      if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
    }
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = '#000';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 44;
    ctx.lineCap = 'round';
    cv._nmCtx = ctx;
    cv._nmPainted = true;
    if (cv.parentElement) cv.parentElement.classList.add('nm-ready'); // pas nu mag de prijs eronder zichtbaar worden
  }

  function bindScratch(cv, pop) {
    if (cv._nmBound) return;
    cv._nmBound = true;
    // maat veranderd (telefoon gedraaid, venster, of de eerste meting was 0): kraslaag opnieuw tekenen
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => {
        if (pop._nmRevealed || !pop.classList.contains('nm-show')) return;
        const W = cv.offsetWidth, H = cv.offsetHeight;
        if (!W || !H) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        if (cv._nmPainted && Math.abs(cv.width - Math.round(W * dpr)) < 2 && Math.abs(cv.height - Math.round(H * dpr)) < 2) return;
        cv._nmLast = null;
        paintScratch(cv, 0);
        if (cv.parentElement) cv.parentElement.classList.remove('nm-touched');
      }).observe(cv);
    }
    let drawing = false;
    let last = 0;
    const at = (x, y) => {
      const ctx = cv._nmCtx;
      if (!ctx || pop._nmRevealed) return;
      const r = cv.getBoundingClientRect();
      const px = (x - r.left) * (r.width ? cv.offsetWidth / r.width : 1);
      const py = (y - r.top) * (r.height ? cv.offsetHeight / r.height : 1);
      if (cv._nmLast) {
        ctx.beginPath();
        ctx.moveTo(cv._nmLast[0], cv._nmLast[1]);
        ctx.lineTo(px, py);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.arc(px, py, 22, 0, 7);
      ctx.fill();
      cv._nmLast = [px, py];
      const now = performance.now();
      if (now - last < 150) return;
      last = now;
      let data;
      try { data = ctx.getImageData(0, 0, cv.width, cv.height).data; } catch (e) { return; }
      let clear = 0;
      let total = 0;
      for (let i = 3; i < data.length; i += 64) {
        total++;
        if (data[i] === 0) clear++;
      }
      if (total && clear / total > 0.42) scratchReveal(pop);
    };
    cv.addEventListener('pointerdown', (e) => {
      drawing = true;
      if (cv.parentElement) cv.parentElement.classList.add('nm-touched'); // hint-handje weg
      try { cv.setPointerCapture(e.pointerId); } catch (x) { /* */ }
      at(e.clientX, e.clientY);
    });
    cv.addEventListener('pointermove', (e) => { if (drawing || e.pointerType === 'mouse') at(e.clientX, e.clientY); });
    const stop = () => { drawing = false; cv._nmLast = null; };
    cv.addEventListener('pointerup', stop);
    cv.addEventListener('pointercancel', stop);
    cv.addEventListener('pointerleave', stop);
    cv.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        scratchReveal(pop);
      }
    });
  }

  function scratchReveal(pop) {
    if (pop._nmRevealed) return;
    pop._nmRevealed = true;
    pop.classList.add('nm-won');
    const cv = pop.querySelector('[data-nm-scratch]');
    if (cv) {
      cv.style.opacity = '0';
      cv.style.pointerEvents = 'none';
      cv.setAttribute('tabindex', '-1');
    }
    const claim = pop.querySelector('[data-nm-claim]');
    if (claim) {
      claim.classList.add('nm-show');
      // kleine schermen: e-mailveld en knop in beeld schuiven (mobiel scrolt de kaart, desktop de tekstkolom)
      setTimeout(() => {
        const btn = claim.querySelector('[type="submit"]') || claim;
        let sc = claim.parentElement;
        while (sc && sc !== pop && !(sc.scrollHeight > sc.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(sc).overflowY))) sc = sc.parentElement;
        if (!sc || sc === pop) return;
        const over = btn.getBoundingClientRect().bottom - sc.getBoundingClientRect().bottom + 20;
        if (over > 0) sc.scrollTo({ top: sc.scrollTop + over, behavior: RM ? 'auto' : 'smooth' });
      }, 480);
    }
    confetti();
  }

  function confetti() {
    if (RM) return;
    const cs = getComputedStyle(html);
    const cols = ['#BF9B5E', '#E6CF9F', '#A9803F', '#F3E6C4', '#5A1628'];
    void cs;
    const c = d.createElement('div');
    c.className = 'nm-confetti';
    c.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 46; i++) {
      const s = d.createElement('i');
      s.style.left = 40 + Math.random() * 20 + '%';
      s.style.top = '42%';
      s.style.background = cols[i % 5];
      s.style.setProperty('--nm-x', Math.random() * 600 - 300 + 'px');
      s.style.setProperty('--nm-y', Math.random() * 500 - 150 + 'px');
      s.style.setProperty('--nm-rot', Math.random() * 720 + 'deg');
      s.style.animationDelay = Math.random() * 0.15 + 's';
      c.appendChild(s);
    }
    d.body.appendChild(c);
    setTimeout(() => c.remove(), 2000);
  }

  function initPop(pop) {
    const cv = pop.querySelector('[data-nm-scratch]');
    if (cv) bindScratch(cv, pop);
    if (DESIGN) return; // in de editor opent hij via shopify:section:select
    if (store.get('localStorage', K_CLAIMED)) return;
    if (seenRecently()) { showUnlock(true); return; }
    clearTimeout(pop._nmTimer);
    const raw = pop.getAttribute('data-nm-pop-delay');
    const delay = raw === null || raw === '' || isNaN(Number(raw)) ? 7000 : Number(raw);
    const tryOpen = () => {
      if (!d.contains(pop) || pop.classList.contains('nm-show') || seenRecently()) return;
      if (d.body.classList.contains('nm-cart')) { pop._nmTimer = setTimeout(tryOpen, 5000); return; }
      openPop(pop);
    };
    pop._nmTimer = setTimeout(tryOpen, delay);
  }

  function claimDone(pop) {
    store.set('localStorage', K_CLAIMED, '1');
    showUnlock(false);
    const code = pop.getAttribute('data-nm-code');
    if (code && pop.getAttribute('data-nm-apply-code') === 'true') {
      fetch(ROOT + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(ROOT + 'cart.js'), { credentials: 'same-origin' }).catch(() => {});
    }
  }

  /* ---------- e-mailformulieren (Shopify customer-form via fetch) ---------- */
  function newsMsgEl(form) {
    return form.querySelector('[data-nm-news-msg]') || (form.parentElement && form.parentElement.querySelector('[data-nm-news-msg]'));
  }

  function newsMsg(form, text, isErr) {
    const m = newsMsgEl(form);
    if (m) {
      m.textContent = text;
      m.hidden = false;
      m.classList.toggle('nm-err', !!isErr);
      m.classList.add('nm-show');
    } else if (isErr) {
      flash(form.querySelector('[type="submit"], button:not([type])'), text);
    }
  }

  function newsDone(form, btn) {
    const pop = form.closest('[data-nm-pop]');
    const m = newsMsgEl(form);
    if (m) newsMsg(form, m.getAttribute('data-ok') || m.getAttribute('data-nm-ok') || CFG.t.newsOk, false);
    const okText = btn && btn.getAttribute('data-nm-ok');
    if (btn && okText) btn.textContent = okText;
    if (!pop) {
      const email = form.querySelector('input[type="email"]');
      if (email) email.value = '';
    }
    form.dispatchEvent(new CustomEvent('nm:news:success', { bubbles: true }));
    if (pop) { const box = pop.querySelector('[data-nm-code-box]'); if (box) box.hidden = false; }
    if (pop) claimDone(pop);
  }

  async function submitNews(form) {
    if (form._nmBusy) return;
    form._nmBusy = true;
    const btn = form.querySelector('[type="submit"], button:not([type])');
    if (btn) btn.classList.add('nm-loading');
    try {
      const res = await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin', headers: { Accept: 'text/html' } });
      const url = String(res.url || '');
      if (/\/challenge/.test(url)) {
        // Shopify vraagt om een captcha: dan gewoon het formulier versturen
        form.removeAttribute('data-nm-news-form');
        HTMLFormElement.prototype.submit.call(form);
        return;
      }
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const path = url.replace(/^https?:\/\/[^/]+/, '').split('#')[0];
      const posted = /[?&](customer|contact)_posted=true/.test(url);
      if (!posted && /\/contact\/?(\?|$)/.test(path)) {
        newsMsg(form, CFG.t.newsError, true);
        return;
      }
      newsDone(form, btn);
    } catch (e) {
      newsMsg(form, CFG.t.error, true);
    } finally {
      form._nmBusy = false;
      if (btn) btn.classList.remove('nm-loading');
    }
  }

  /* ---------- kraskaart: e-mail naar Klaviyo (die mailt de code), dan Shopify, dan de code tonen ---------- */
  function klaviyo(company, path, body) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
      body: JSON.stringify(body)
    });
  }

  async function submitPop(form, pop) {
    if (form._nmBusy) return;
    const input = form.querySelector('input[type="email"]');
    const err = pop.querySelector('[data-nm-pop-err]');
    const email = ((input && input.value) || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      if (err) err.hidden = false;
      if (input) input.focus();
      return;
    }
    if (err) err.hidden = true;
    form._nmBusy = true;
    const btn = form.querySelector('[type="submit"], button:not([type])');
    if (btn) btn.classList.add('nm-loading');
    const company = pop.getAttribute('data-nm-kl-company') || '';
    const list = pop.getAttribute('data-nm-kl-list') || '';
    const code = pop.getAttribute('data-nm-code') || '';
    let mailed = false;
    if (company && list) {
      try {
        const props = { sfuma_bron: 'kraskaart', sfuma_kraskaart_op: new Date().toISOString() };
        if (code) props.sfuma_code = code; // in de stand 'code alleen per mail' staat de code niet op de pagina
        await klaviyo(company, 'profiles', { data: { type: 'profile', attributes: { email: email, properties: props } } }).catch(() => {});
        const res = await klaviyo(company, 'subscriptions', { data: { type: 'subscription', attributes: { custom_source: 'SFUMA kraskaart', profile: { data: { type: 'profile', attributes: { email: email } } } }, relationships: { list: { data: { type: 'list', id: list } } } } });
        mailed = !!(res && (res.ok || res.status === 202));
      } catch (e) { mailed = false; }
    }
    let shopOk = false;
    try {
      // Shopify-klant met marketingtoestemming; bij een captcha niet wegnavigeren, Klaviyo heeft het adres al
      const r = await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin', headers: { Accept: 'text/html' } });
      shopOk = !!(r && r.ok);
    } catch (e) { /* */ }
    form._nmBusy = false;
    if (btn) btn.classList.remove('nm-loading');
    const mailOnly = pop.getAttribute('data-nm-mail-only') === 'true';
    if (mailOnly && !mailed && !shopOk) {
      // de code gaat alleen per mail: lukt de aanmelding niet, dan geen 'gelukt' tonen
      if (err) { err.textContent = CFG.t.error; err.hidden = false; }
      return;
    }
    const note = pop.querySelector('[data-nm-code-note]');
    if (note) note.textContent = (mailed ? note.getAttribute('data-mail') : note.getAttribute('data-nomail')) || note.textContent;
    const claim = pop.querySelector('[data-nm-claim]');
    if (claim) claim.classList.add('nm-done');
    newsDone(form, btn);
    const next = pop.querySelector('[data-nm-code-copy]') || pop.querySelector('[data-nm-code-box] .nm-code-go');
    if (next) setTimeout(() => focusSoft(next), 60);
  }

  d.addEventListener('click', (e) => {
    const b = e.target && e.target.closest && e.target.closest('[data-nm-code-copy]');
    if (!b) return;
    const pop = b.closest('[data-nm-pop]');
    const code = (pop && pop.getAttribute('data-nm-code')) || '';
    const lab = b.querySelector('[data-nm-copy-label]');
    const done = () => { if (lab) lab.textContent = lab.getAttribute('data-copied') || lab.textContent; };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(done).catch(done);
    else done();
  });

  d.addEventListener('submit', (e) => {
    const form = e.target && e.target.closest && e.target.closest('[data-nm-news-form]');
    if (!form || !window.fetch || !window.FormData) return;
    e.preventDefault();
    const pop = form.closest('[data-nm-pop]');
    if (pop) submitPop(form, pop);
    else submitNews(form);
  });

  /* ---------- mobiel menu ---------- */
  function toggleMenu(btn, force) {
    const scope = (btn && btn.closest('.nm')) || d;
    const nav = $('[data-nm-mnav]', scope);
    if (!nav) return;
    const open = typeof force === 'boolean' ? force : !nav.classList.contains('nm-open');
    nav.classList.toggle('nm-open', open);
    $$('[data-nm-menu-toggle]', scope).forEach((b) => b.setAttribute('aria-expanded', open ? 'true' : 'false'));
  }

  /* ---------- klikken (event delegation) ---------- */
  d.addEventListener('click', (e) => {
    const t = e.target;
    if (!t || !t.closest) return;
    let el;
    if ((el = t.closest('[data-nm-cookie-prefs]'))) {
      const pb = window.privacyBanner;
      if (pb && typeof pb.showPreferences === 'function') {
        e.preventDefault();
        pb.showPreferences();
      }
      return;
    }

    if ((el = t.closest('[data-nm-atc], [data-nm-atc-main]'))) {
      e.preventDefault();
      addToCart(el);
      return;
    }
    if ((el = t.closest('[data-nm-bundles] .nm-tile'))) {
      selectTile(el, true);
      return;
    }
    if ((el = t.closest('[data-nm-cart-open]'))) {
      if (drawer()) {
        e.preventDefault();
        openCart(true);
      }
      return;
    }
    if (t.closest('[data-nm-cart-close], [data-nm-scrim]')) {
      e.preventDefault();
      closeCart();
      return;
    }
    if ((el = t.closest('[data-nm-qty]'))) {
      e.preventDefault();
      changeQty(el);
      return;
    }
    if ((el = t.closest('[data-nm-remove]'))) {
      e.preventDefault();
      removeLine(el);
      return;
    }
    if ((el = t.closest('[data-nm-upsell]'))) {
      const btn = t.closest('button');
      if (btn) {
        e.preventDefault();
        doUpsell(el, btn);
      }
      return;
    }
    if ((el = t.closest('.nm-acc-h'))) {
      e.preventDefault();
      toggleAcc(el);
      return;
    }
    if ((el = t.closest('[data-nm-gallery] .nm-th'))) {
      const g = el.closest('[data-nm-gallery]');
      galGo(g, $$('.nm-th', g).indexOf(el));
      return;
    }
    if ((el = t.closest('[data-nm-gal-prev], [data-nm-gal-next]'))) {
      const g = el.closest('[data-nm-gallery]');
      if (g) galGo(g, galIndex(g) + (el.hasAttribute('data-nm-gal-next') ? 1 : -1));
      return;
    }
    if ((el = t.closest('[data-nm-ann-prev], [data-nm-ann-next]'))) {
      const a = el.closest('[data-nm-ann]');
      if (a) annGo(a, el.hasAttribute('data-nm-ann-next') ? 1 : -1);
      return;
    }
    if ((el = t.closest('[data-nm-dots] > *'))) {
      const dots = el.parentElement;
      const s = sliderFor(dots);
      if (s) {
        s._nmHold = Date.now();
        sliderGo(s, Array.prototype.indexOf.call(dots.children, el));
      }
      return;
    }
    if ((el = t.closest('[data-nm-unlock]'))) {
      e.preventDefault();
      if (t.closest('[data-nm-unlock-close]')) {
        el.classList.remove('nm-show');
        store.set('sessionStorage', K_UNLOCK, '1');
      } else {
        openPop();
      }
      return;
    }
    if (t.closest('[data-nm-pop-open]')) {
      e.preventDefault();
      openPop();
      return;
    }
    if (t.closest('[data-nm-pop-close]')) {
      e.preventDefault();
      closePop(t.closest('[data-nm-pop]'));
      return;
    }
    if (t.matches('[data-nm-pop]')) {
      closePop(t);
      return;
    }
    if ((el = t.closest('[data-nm-menu-toggle]'))) {
      e.preventDefault();
      toggleMenu(el);
      return;
    }
    if ((el = t.closest('[data-nm-mnav] a'))) toggleMenu(el, false);
  });

  d.addEventListener('change', (e) => {
    const t = e.target;
    if (t && t.matches && t.matches('[data-nm-gift]')) setGift(t);
  });

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function trapFocus(e) {
    const box = $('[data-nm-pop].nm-show') || (d.body.classList.contains('nm-cart') ? drawer() : null);
    if (!box) return;
    const els = $$(FOCUSABLE, box).filter((el) => el.getClientRects().length && el.getAttribute('tabindex') !== '-1');
    if (!els.length) return;
    const first = els[0];
    const last = els[els.length - 1];
    if (!box.contains(d.activeElement)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && d.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && d.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  d.addEventListener('keydown', (e) => {
    if (e.key === 'Tab') { trapFocus(e); return; }
    if (e.key !== 'Escape') return;
    const pop = $('[data-nm-pop].nm-show');
    if (pop) { closePop(pop); return; }
    if (d.body.classList.contains('nm-cart')) { closeCart(); return; }
    $$('[data-nm-mnav].nm-open').forEach((m) => {
      m.classList.remove('nm-open');
      $$('[data-nm-menu-toggle]', m.closest('.nm') || d).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    });
  });

  /* ---------- init ---------- */
  function init(scope) {
    scope = scope || d;
    readConfig();
    within(scope, '.nm-tile[data-variant-id]').forEach((t) => {
      const c = Number(t.getAttribute('data-compare-cents'));
      const p = Number(t.getAttribute('data-price-cents'));
      if (c && c > p) COMPARE[t.getAttribute('data-variant-id')] = c;
    });
    within(scope, '[data-nm-ring]').forEach(prepRing);
    within(scope, '.nm-acc-i').forEach((it) => {
      const h = it.querySelector('.nm-acc-h');
      if (h) h.setAttribute('aria-expanded', it.classList.contains('nm-open') ? 'true' : 'false');
    });
    within(scope, '[data-nm-bundles] .nm-tile').forEach((t) => t.setAttribute('aria-pressed', t.classList.contains('nm-on') ? 'true' : 'false'));
    within(scope, '[data-nm-ann]').forEach(initAnn);
    within(scope, '[data-nm-slider]').forEach(initSlider);
    within(scope, '[data-nm-pop]').forEach(initPop);
    cacheLists();
    initSticky();
    observe(scope);
    onScroll();
    // winkelwagen bijwerken (aantal, upsell) als er al iets in zit
    const count = $('[data-nm-cart-count]');
    if (drawer() && (scope === d || scope.querySelector('[data-nm-drawer]')) && count && Number(count.textContent) > 0) {
      (window.requestIdleCallback || ((f) => setTimeout(f, 300)))(() => refreshCart());
    }
  }

  /* theme editor */
  d.addEventListener('shopify:section:load', (e) => init(e.target));
  d.addEventListener('shopify:section:unload', (e) => {
    $$('[data-nm-ann], [data-nm-slider]', e.target).forEach((el) => clearInterval(el._nmTimer));
    $$('[data-nm-pop]', e.target).forEach((p) => clearTimeout(p._nmTimer));
    if (e.target.querySelector('[data-nm-drawer]')) d.body.classList.remove('nm-cart');
  });
  const editorPreview = (sec, on) => {
    const box = sec && sec.querySelector && sec.querySelector('[data-nm-editor-preview]');
    if (!box) return;
    const mode = box.getAttribute('data-nm-editor-preview');
    if (mode === 'popup') {
      const p = $('[data-nm-pop]', sec);
      if (p) { if (on) openPop(p, true); else closePop(p, true); }
    } else if (mode === 'cart') {
      if (on) openCart(true); else closeCart();
    }
  };
  d.addEventListener('shopify:section:select', (e) => editorPreview(e.target, true));
  d.addEventListener('shopify:section:deselect', (e) => editorPreview(e.target, false));
  d.addEventListener('shopify:block:select', (e) => {
    const a = e.target.closest && e.target.closest('[data-nm-ann]');
    if (a) {
      const s = annSlides(a);
      const i = s ? Array.prototype.indexOf.call(s.children, e.target) : -1;
      if (i > -1) { a._nmHold = true; annGo(a, i, true); }
    }
  });
  d.addEventListener('shopify:block:deselect', (e) => {
    const a = e.target.closest && e.target.closest('[data-nm-ann]');
    if (a) a._nmHold = false;
  });

  /* terug via de browserknop (bfcache): aantal kan verouderd zijn */
  window.addEventListener('pageshow', (e) => { if (e.persisted && drawer()) refreshCart(); });
  /* Horizon-formulieren die zelf toevoegen */
  d.addEventListener('cart:update', () => refreshCart());

  window.NM = { __loaded: true, init, openCart, closeCart, refreshCart, addToCart, money, openPop, closePop };

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', () => init(d));
  else init(d);
})();
